
import { Record } from "../../fable_modules/fable-library-js.5.3.0/Types.js";
import { bool_type, string_type, int32_type, lambda_type, unit_type, record_type, option_type, class_type } from "../../fable_modules/fable-library-js.5.3.0/Reflection.js";
import { toString, equals, op_Subtraction, date, compare } from "../../fable_modules/fable-library-js.5.3.0/Date.js";
import { days as days_1 } from "../../fable_modules/fable-library-js.5.3.0/TimeSpan.js";
import { HtmlHelper_createElement } from "../../fable_modules/Feliz.3.3.3/Html.fs.js";
import { margin_mt1, margin_ml1, gap_gap$002D1, backgroundColor_bgNeutral50, borderRadius_roundedMd, border_border, padding_py1, padding_px3, fontWeight_fontMedium, fontSize_textSm, gap_gap$002D2, alignItems_itemsCenter, display_flex, margin_mt2, tw } from "../../fable_modules/Feliz.Tailwind.4.0.2/Strings.fs.js";
import { textColor_textNeutral600, textColor_textDanger600, hover, borderColor_borderNeutral300, backgroundColor_bgSurfaceElevated, textColor_textNeutral700, textColor_textPrimary700 } from "../TailwindExtensions.js";
import { singleton, ofArray } from "../../fable_modules/fable-library-js.5.3.0/List.js";
import { labelClass } from "./AutoSave.js";
import { empty, singleton as singleton_1, append, delay, toList } from "../../fable_modules/fable-library-js.5.3.0/Seq.js";
import { createElement } from "react";
import React from "react";
import { Props as Props_1, RangeSelection, render as render_1 } from "./DayPicker.js";
import { defaultOf } from "../../fable_modules/fable-library-js.5.3.0/Util.js";

/**
 * User's currently picked range; either bound may be missing during partial selection
 * or when the range has been cleared.
 */
export class SelectedRange extends Record {
    constructor(Start, End) {
        super();
        this.Start = Start;
        this.End = End;
    }
}

export function SelectedRange_$reflection() {
    return record_type("Shared.DateRangeCalendar.SelectedRange", [], SelectedRange, () => [["Start", option_type(class_type("System.DateTime"))], ["End", option_type(class_type("System.DateTime"))]]);
}

export class Props extends Record {
    constructor(Selected, OnChange, OnStartPicked, Today, MaxDate, MinDurationDays, MaxDurationDays, Label, Required) {
        super();
        this.Selected = Selected;
        this.OnChange = OnChange;
        this.OnStartPicked = OnStartPicked;
        this.Today = Today;
        this.MaxDate = MaxDate;
        this.MinDurationDays = (MinDurationDays | 0);
        this.MaxDurationDays = (MaxDurationDays | 0);
        this.Label = Label;
        this.Required = Required;
    }
}

export function Props_$reflection() {
    return record_type("Shared.DateRangeCalendar.Props", [], Props, () => [["Selected", SelectedRange_$reflection()], ["OnChange", lambda_type(SelectedRange_$reflection(), unit_type)], ["OnStartPicked", lambda_type(class_type("System.DateTime"), unit_type)], ["Today", class_type("System.DateTime")], ["MaxDate", class_type("System.DateTime")], ["MinDurationDays", int32_type], ["MaxDurationDays", int32_type], ["Label", string_type], ["Required", bool_type]]);
}

/**
 * Pure helper — returns true if the candidate date should be disabled given a current selection.
 * Past dates, dates beyond MaxDate, and dates outside the [MinDurationDays..MaxDurationDays]
 * window from a partially-picked Start are all disabled.
 * Dates at or before the picked Start are kept enabled so the user can click the start date
 * (or any earlier date) to reset the range and pick a new start.
 */
export function isDisabled(props, candidate) {
    if (compare(date(candidate), date(props.Today)) < 0) {
        return true;
    }
    else if (compare(date(candidate), date(props.MaxDate)) > 0) {
        return true;
    }
    else {
        const matchValue = props.Selected.Start;
        let matchResult, s;
        if (matchValue != null) {
            if (props.Selected.End == null) {
                matchResult = 0;
                s = matchValue;
            }
            else {
                matchResult = 1;
            }
        }
        else {
            matchResult = 1;
        }
        switch (matchResult) {
            case 0: {
                const days = days_1(op_Subtraction(date(candidate), date(s))) | 0;
                if (days > 0) {
                    if (days < props.MinDurationDays) {
                        return true;
                    }
                    else {
                        return days > props.MaxDurationDays;
                    }
                }
                else {
                    return false;
                }
            }
            default:
                return false;
        }
    }
}

/**
 * Pure helper — applies re-pick semantics to a range selection.
 * When the user already has a complete range (Start + End) and clicks a new date,
 * the click starts a fresh range from the clicked date instead of extending the
 * existing range (which is react-day-picker's default behavior).
 * When from = to (re-click on start), collapses End to None so a 0-day range
 * never enters form state.
 */
export function applyRepick(current, rawFrom, rawTo, triggerDate) {
    let t, f_1, t_1;
    let matchResult, clicked;
    if (current.Start != null) {
        if (current.End != null) {
            if (triggerDate != null) {
                matchResult = 0;
                clicked = triggerDate;
            }
            else {
                matchResult = 1;
            }
        }
        else {
            matchResult = 1;
        }
    }
    else {
        matchResult = 1;
    }
    switch (matchResult) {
        case 0:
            return new SelectedRange(clicked, undefined);
        default:
            return new SelectedRange(rawFrom, (rawFrom != null) ? ((rawTo != null) ? (((t = rawTo, equals(date(rawFrom), date(t)))) ? ((f_1 = rawFrom, (t_1 = rawTo, undefined))) : rawTo) : rawTo) : rawTo);
    }
}

/**
 * Pure helper — derives the duration in days from a complete range.
 * Returns None if either bound is missing.
 */
export function durationDays(sel) {
    const matchValue = sel.Start;
    const matchValue_1 = sel.End;
    let matchResult, e, s;
    if (matchValue != null) {
        if (matchValue_1 != null) {
            matchResult = 0;
            e = matchValue_1;
            s = matchValue;
        }
        else {
            matchResult = 1;
        }
    }
    else {
        matchResult = 1;
    }
    switch (matchResult) {
        case 0:
            return days_1(op_Subtraction(date(e), date(s)));
        default:
            return undefined;
    }
}

function formatShort(d) {
    return toString(d, "MMM d");
}

function helperText(props) {
    return `Pick when your challenge starts and ends. Challenges run for ${props.MinDurationDays}–${props.MaxDurationDays} days.`;
}

function summaryLine(props) {
    const matchValue = props.Selected.Start;
    const matchValue_1 = props.Selected.End;
    const matchValue_2 = durationDays(props.Selected);
    let matchResult, d, e, s;
    if (matchValue != null) {
        if (matchValue_1 != null) {
            if (matchValue_2 != null) {
                matchResult = 0;
                d = matchValue_2;
                e = matchValue_1;
                s = matchValue;
            }
            else {
                matchResult = 1;
            }
        }
        else {
            matchResult = 1;
        }
    }
    else {
        matchResult = 1;
    }
    switch (matchResult) {
        case 0:
            return HtmlHelper_createElement("div", ofArray([["className", tw(ofArray([margin_mt2, display_flex, alignItems_itemsCenter, gap_gap$002D2, fontSize_textSm, textColor_textPrimary700]))], ["children", [HtmlHelper_createElement("span", ofArray([["className", tw(singleton(fontWeight_fontMedium))], ["children", singleton(`✓ ${formatShort(s)} → ${formatShort(e)} · ${d} days`)]]))]]]));
        default:
            return undefined;
    }
}

function clearButton(props, onClear) {
    return HtmlHelper_createElement("button", ofArray([["type", "button"], ["className", tw(ofArray([margin_mt2, padding_px3, padding_py1, fontSize_textSm, fontWeight_fontMedium, textColor_textNeutral700, backgroundColor_bgSurfaceElevated, border_border, borderColor_borderNeutral300, borderRadius_roundedMd, hover(backgroundColor_bgNeutral50)]))], ["children", singleton("Clear range")], ["onClick", (_arg) => {
        onClear();
    }]]));
}

function labelRow(props) {
    return HtmlHelper_createElement("div", ofArray([["className", tw(ofArray([display_flex, alignItems_itemsCenter, gap_gap$002D1]))], ["children", [HtmlHelper_createElement("div", ofArray([["className", labelClass], ["children", toList(delay(() => append(singleton_1(props.Label), delay(() => (props.Required ? singleton_1(HtmlHelper_createElement("span", ofArray([["aria-hidden", "true"], ["className", tw(ofArray([textColor_textDanger600, margin_ml1]))], ["children", singleton("*")]]))) : empty())))))]]))]]]));
}

function helperParagraph(props) {
    return HtmlHelper_createElement("p", ofArray([["className", tw(ofArray([margin_mt1, fontSize_textSm, textColor_textNeutral600]))], ["children", singleton(helperText(props))]]));
}

export function DateRangeCalendarComponent($props) {
    const props = $props.props;
    const clearRange = () => {
        props.OnChange(new SelectedRange(undefined, undefined));
    };
    return HtmlHelper_createElement("div", singleton(["children", toList(delay(() => append(singleton_1(labelRow(props)), delay(() => append(singleton_1(helperParagraph(props)), delay(() => append(singleton_1(HtmlHelper_createElement("div", ofArray([["className", tw(singleton(margin_mt2))], ["onKeyDown", (e) => {
        if (e.key === "Escape") {
            e.preventDefault();
            clearRange();
        }
    }], ["children", [render_1(new Props_1(new RangeSelection(props.Selected.Start, props.Selected.End), (sel, triggerDate) => {
        const selected = applyRepick(props.Selected, sel.From, sel.To, triggerDate);
        const matchValue = selected.Start;
        let matchResult, s;
        if (matchValue != null) {
            if (selected.End == null) {
                matchResult = 0;
                s = matchValue;
            }
            else {
                matchResult = 1;
            }
        }
        else {
            matchResult = 1;
        }
        switch (matchResult) {
            case 0: {
                props.OnStartPicked(s);
                break;
            }
        }
        props.OnChange(selected);
    }, (candidate) => isDisabled(props, candidate), props.Today, props.MaxDate, 2))]]]))), delay(() => {
        let matchValue_3;
        return append((matchValue_3 = summaryLine(props), (matchValue_3 == null) ? singleton_1(defaultOf()) : singleton_1(matchValue_3)), delay(() => ((props.Selected.End != null) ? singleton_1(clearButton(props, clearRange)) : singleton_1(defaultOf()))));
    }))))))))]));
}

export function render(props) {
    return createElement(DateRangeCalendarComponent, {
        props: props,
    });
}

//# sourceMappingURL=DateRangeCalendar.js.map
